import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import api from '../../api';

const EditStaffModal = ({
  open,
  editUser,
  onClose,
  onChange,
  onSave,
  isProcessing,
  roles,
  tenantId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(editUser || {});
  const [phoneValue, setPhoneValue] = useState('');

  const normalizeRoleKey = (role) => {
    if (!role) return '';
    const normalized = String(role).trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (normalized === 'controller_of_examination') return 'coe';
    return normalized;
  };

  const getRoleDisplayLabel = (role) => {
    const normalized = normalizeRoleKey(role);
    if (normalized === 'coe') return 'Controller of Examination';
    if (normalized === 'coe_coordinator') return 'COE Coordinator';

    return String(role || '')
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const roleOptions = (() => {
    const incomingRoles = Array.isArray(roles) ? roles : [];
    const map = new Map();

    incomingRoles.forEach((role) => {
      const normalized = normalizeRoleKey(role);
      if (normalized) {
        map.set(normalized, getRoleDisplayLabel(role));
      }
    });

    if (!map.has('coe_coordinator')) {
      map.set('coe_coordinator', 'COE Coordinator');
    }

    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  })();
  
  // Use ref to track if initial data has been loaded
  const initialDataLoadedRef = useRef(false);
  // Use ref to store the initial user ID to detect changes
  const currentUserIdRef = useRef(editUser?._id);

  // Fetch combined user data only when modal opens or user changes
  useEffect(() => {
    const fetchUserData = async () => {
      // Only fetch if modal is open, we have userId and tenantId, and either:
      // 1. Initial data hasn't been loaded yet, OR
      // 2. The user ID has changed
      const shouldFetch = open && editUser?._id && tenantId && 
        (!initialDataLoadedRef.current || currentUserIdRef.current !== editUser._id);
      
      if (!shouldFetch) {
        // If we already have data but modal is open, just use existing editUser
        if (open && editUser) {
          console.log('Using existing editUser data:', editUser);
          setUserData(editUser);
          setPhoneValue(editUser?.phoneNumber || '');
        }
        return;
      }
      
      console.log('Fetching fresh user data for edit:', { userId: editUser._id, tenantId });
      
      setLoading(true);
      setError('');
      
      try {
        const response = await api.get('/api/admin/staff/edit-details', {
          params: {
            userId: editUser._id,
            tenantId: tenantId
          }
        });
        
        console.log('Edit user data response:', response.data);
        
        if (response.data.success) {
          const phoneNumber = response.data.user.phoneNumber || '';
          
          const newUserData = {
            _id: editUser._id,
            firstName: response.data.user.firstName || '',
            lastName: response.data.user.lastName || '',
            email: response.data.auth.email || '',
            phoneNumber: phoneNumber,
            role: normalizeRoleKey(response.data.auth.tenantRole?.role || response.data.user.role || '')
          };
          
          console.log('Setting user data:', newUserData);
          setUserData(newUserData);
          setPhoneValue(phoneNumber);
          
          // Update the refs
          initialDataLoadedRef.current = true;
          currentUserIdRef.current = editUser._id;
        } else {
          setError(response.data.message || 'Failed to load user details');
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        if (error.response) {
          setError(error.response.data.message || 'Failed to load user details');
        } else if (error.request) {
          setError('No response from server. Please check your connection.');
        } else {
          setError('An error occurred. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    
    // Reset refs when modal closes
    if (!open) {
      initialDataLoadedRef.current = false;
      currentUserIdRef.current = null;
    }
  }, [open, editUser, tenantId]); // Dependencies include editUser

  const handleChange = (field, value) => {
    console.log(`Changing ${field} to:`, value);
    const updatedUser = { ...userData, [field]: value };
    setUserData(updatedUser);
    onChange(updatedUser); // Notify parent of changes
    if (error) setError('');
  };

  const handlePhoneChange = (value) => {
    console.log('Phone changed to:', value);
    setPhoneValue(value);
    const updatedUser = { ...userData, phoneNumber: value };
    setUserData(updatedUser);
    onChange(updatedUser);
  };

  const validateForm = () => {
    if (!userData.firstName?.trim()) {
      setError('First name is required');
      return false;
    }
    
    if (!userData.email?.trim()) {
      setError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (!userData.phoneNumber) {
      setError('Phone number is required');
      return false;
    }
    
    // Basic phone validation - should have at least 10 digits
    const digitsOnly = userData.phoneNumber.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      setError('Phone number must have at least 10 digits');
      return false;
    }
    
    if (!userData.role) {
      setError('Please select a role');
      return false;
    }
    
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    console.log('Saving user data:', userData);
    onSave(userData);
  };

  const handleClose = () => {
    // Reset refs when closing
    initialDataLoadedRef.current = false;
    currentUserIdRef.current = null;
    onClose();
  };

  if (!open) return null;

  return (
    <Modal 
      title={`Edit ${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Staff Member'} 
      onClose={handleClose}
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userData.firstName || ''}
                onChange={e => handleChange('firstName', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter first name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={userData.lastName || ''}
                onChange={e => handleChange('lastName', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter last name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={userData.email || ''}
                onChange={e => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter email address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                international
                defaultCountry="IN"
                value={phoneValue}
                onChange={handlePhoneChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter phone number with country code"
                disabled={isProcessing || loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: +91 9876543210 (India) or +1 234 567 8900 (US)
              </p>
              {phoneValue && (
                <p className="text-xs text-green-600 mt-1">
                  Current: {phoneValue}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={userData.role || ''}
                onChange={e => handleChange('role', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select a role</option>
                {roleOptions.map((role, index) => (
                  <option key={index} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isProcessing || loading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isProcessing || loading}
                className={`px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white ${
                  isProcessing || loading
                    ? 'bg-blue-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50`}
              >
                {isProcessing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default EditStaffModal;